import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const LANGUAGE_NAMES: Record<string, string> = {
  ar: "Arabic",
  es: "Spanish",
  fr: "French",
  zh: "Chinese",
  ja: "Japanese",
  de: "German",
  pt: "Portuguese",
  ru: "Russian",
  hi: "Hindi",
  no: "Norwegian Bokmal",
  "zh-Hant": "Traditional Chinese (Hong Kong / Taiwan usage, traditional characters only, never simplified)",
};

/** Locales the site itself is published in, translatable without signing in. */
const SITE_LANGUAGES = new Set(["no", "zh-Hant"]);

/** Strip publisher clutter out of a scraped article and cache the result. */
export const cleanArticle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { articleId: string; force?: boolean }) => input)
  .handler(async ({ data }) => {
    const { cleanArticleById } = await import("./articleAi.server");
    if (!UUID_RE.test(data.articleId)) throw new Error("Valid articleId is required");
    try {
      return await cleanArticleById(data.articleId, data.force === true);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Article cleanup failed";
      const isTemporaryAiFailure =
        message.includes("rate limit") ||
        message.includes("temporarily unavailable") ||
        message.includes("AI service is unavailable") ||
        message.startsWith("Gemini 5");

      // AI cleanup is an enhancement. The article page already has a deterministic
      // cleaner, so a temporary provider failure must never reject the server call.
      if (isTemporaryAiFailure) {
        return { cached: false, content: null, reason: "AI cleanup temporarily unavailable" };
      }

      throw error;
    }
  });

export interface TranslateArticleArgs {
  articleId: string;
  languageCode: string;
  title: string;
  excerpt: string;
  content?: string | null;
}

/** Translate an article, reusing the cached translation when one exists. */
export const translateArticle = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: TranslateArticleArgs) => input)
  .handler(async ({ data }) => {
    const { translateArticleInto } = await import("./articleAi.server");

    if (!data.articleId || !data.languageCode || !data.title || !data.excerpt) {
      throw new Error("Missing required parameters");
    }
    if (data.languageCode === "en") {
      throw new Error("English is the original language, no translation needed");
    }
    const languageName = LANGUAGE_NAMES[data.languageCode];
    if (!languageName) throw new Error(`Unsupported language code: ${data.languageCode}`);

    return translateArticleInto(data, languageName);
  });

/**
 * Site-language translation of a published article for anonymous readers.
 * Only the locales the site is published in are allowed; results are cached.
 */
export const translateArticleForSite = createServerFn({ method: "POST" })
  .inputValidator((input: TranslateArticleArgs) => {
    if (!UUID_RE.test(input?.articleId ?? "")) throw new Error("Invalid article id");
    if (!SITE_LANGUAGES.has(input?.languageCode ?? "")) throw new Error("Unsupported language");
    return input;
  })
  .handler(async ({ data }) => {
    const { translateArticleInto } = await import("./articleAi.server");
    const languageName = LANGUAGE_NAMES[data.languageCode]!;
    return translateArticleInto(data, languageName);
  });

/** Saved full translation of one story (no AI call, no sign-in). */
export const getSavedArticleTranslation = createServerFn({ method: "POST" })
  .inputValidator((input: { articleId: string; language: string }) => {
    if (!UUID_RE.test(input?.articleId ?? "")) throw new Error("Invalid article id");
    if (!SITE_LANGUAGES.has(input?.language ?? "")) throw new Error("Unsupported language");
    return input;
  })
  .handler(async ({ data }) => {
    const { getFullTranslation } = await import("./articleAi.server");
    return { translation: await getFullTranslation(data.articleId, data.language) };
  });

/**
 * Same lookup, but by the address in the URL (slug or id) so a page can load
 * the reader's language on the server — no English flash while it fetches.
 */
export const getSavedArticleTranslationByPath = createServerFn({ method: "GET" })
  .inputValidator((input: { path: string; language: string }) => {
    const path = (input?.path ?? "").trim().slice(0, 300);
    if (!path) throw new Error("Invalid article path");
    if (!SITE_LANGUAGES.has(input?.language ?? "")) throw new Error("Unsupported language");
    return { path, language: input.language };
  })
  .handler(async ({ data }) => {
    const { getFullTranslation } = await import("./articleAi.server");
    const { supabaseAnon } = await import("@/lib/mcp/supabase");
    const db = supabaseAnon();
    const isUuid = UUID_RE.test(data.path);
    const { data: row } = await db
      .from("articles")
      .select("id")
      .eq(isUuid ? "id" : "slug", data.path)
      .limit(1)
      .maybeSingle();
    if (!row?.id) return { articleId: null, translation: null };
    return {
      articleId: row.id as string,
      translation: await getFullTranslation(row.id as string, data.language),
    };
  });

export interface HeadlineLookupArgs {
  ids: string[];
  language: string;
}

const validateHeadlineArgs = (input: HeadlineLookupArgs) => {
  if (!SITE_LANGUAGES.has(input?.language ?? "")) throw new Error("Unsupported language");
  const ids = (input?.ids ?? []).filter((id) => UUID_RE.test(id)).slice(0, 60);
  return { ids, language: input.language };
};

/** Saved headline/summary translations for a batch of stories (no AI call). */
export const getArticleTranslations = createServerFn({ method: "POST" })
  .inputValidator(validateHeadlineArgs)
  .handler(async ({ data }) => {
    const { getCachedHeadlines } = await import("./articleAi.server");
    return { items: await getCachedHeadlines(data.ids, data.language) };
  });

/** Translate the headline and summary of stories a reader is looking at. */
export const translateHeadlines = createServerFn({ method: "POST" })
  .inputValidator(validateHeadlineArgs)
  .handler(async ({ data }) => {
    const { translateHeadlinesBatch } = await import("./articleAi.server");
    const languageName = LANGUAGE_NAMES[data.language]!;
    try {
      return { items: await translateHeadlinesBatch(data.ids, data.language, languageName) };
    } catch (error) {
      // A translation hiccup must never break the feed; the English text stays.
      console.error("headline translation failed", error);
      return { items: [] };
    }
  });



/** Admin: write a fresh excerpt for one article. */
export const generateArticleExcerpt = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { articleId: string; regenerate?: boolean }) => input)
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./authz.server");
    const { generateExcerptFor } = await import("./articleAi.server");

    await assertAdmin(context.supabase, context.userId);
    if (!data.articleId) throw new Error("Article ID is required");

    return generateExcerptFor(data.articleId, data.regenerate === true);
  });

/** Admin: rewrite excerpts in bulk for articles whose excerpt is missing or poor. */
export const batchGenerateExcerpts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: { batchSize?: number; limit?: number; onlyBadExcerpts?: boolean }) => input,
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./authz.server");
    const { runBatchExcerpts } = await import("./articleAi.server");

    await assertAdmin(context.supabase, context.userId);
    return runBatchExcerpts({
      batchSize: data.batchSize ?? 10,
      limit: data.limit ?? 100,
      onlyBadExcerpts: data.onlyBadExcerpts ?? true,
    });
  });

export interface RewriteArticleResult {
  success: boolean;
  content?: string;
  sourceName?: string;
  sourceUrl?: string;
  cached?: boolean;
  error?: string;
}

/**
 * Byteline write-up for stories whose full text cannot be fetched.
 * Public: the first visitor triggers the write-up, everyone after reads the cached one.
 */
export const rewriteArticle = createServerFn({ method: "POST" })
  .inputValidator((input: { articleId: string }) => {
    if (!UUID_RE.test(input?.articleId ?? "")) throw new Error("Invalid article id");
    return { articleId: input.articleId };
  })
  .handler(async ({ data }): Promise<RewriteArticleResult> => {
    const { rewriteArticleById } = await import("./articleRewrite.server");
    try {
      return await rewriteArticleById(data.articleId);
    } catch (error) {
      console.error("rewriteArticle failed", error);
      return { success: false, error: "Could not write this story" };
    }
  });
